import {TSTypes, EOType, Guid, ItemCode, WarehouseCode, ApiFields,
  CalcFieldTypeMapEntry, AnyCalcTypeMap,
  CalcFieldTypeMap, ApiObjectCalcField} from "./types"
import {ISODate} from "../date"
import {TSAssertType} from "../tsmagic"

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type TypeMap<TSType extends TSTypes> = {
  fromEO: (data: EOType<TSType>) => TSType
  toEOFilterValue: (data: TSType) => string
  toEOUpdateValue: (data: TSType) => string
}

export type UpdateableTypeMap = {
  updateable: true
}

export function updateableTM<
  T extends TypeMap<any> | CalcFieldTypeMapEntry<AnyCalcTypeMap>>(
  typeMap: T,
) : T & UpdateableTypeMap {
  return {
    ...typeMap,
    updateable: true
  }
}

export type UpdateSignature<FIELDS extends ApiFields, CALCFIELDS extends CalcFieldTypeMap | null> = Partial<{
    [key in keyof FIELDS as FIELDS[key] extends UpdateableTypeMap ? key : never]:
  Readonly<ReturnType<FIELDS[key]["fromEO"]>>
} & (CALCFIELDS extends null ? {} : {
    [key in keyof CALCFIELDS as CALCFIELDS[key] extends UpdateableTypeMap
      ? (key extends string ? `$calc.${key}` : never) : never]:
  ApiObjectCalcField<NonNullable<CALCFIELDS>>["$calc"][key]
})>

export function assertTM<T>(premise: (d: T) => boolean, data: T): T {
  if (!premise(data)) {
    throw new Error(`Assert failed: ${premise.toString()} - ${JSON.stringify(data)}`)
  }
  return data
}

export function copyFilterEOValueToUpdateValue<T extends TSTypes>(
  tm: Omit<TypeMap<T>, "toEOUpdateValue">): TypeMap<T> {
    return {
      ...tm,
      toEOUpdateValue: tm.toEOFilterValue
    }
}

export const NullableStringTypeMap: TypeMap<string|null> =
  copyFilterEOValueToUpdateValue({
    fromEO: (data: string | null) => assertTM(
      d => d === null || typeof d === "string", data),
    // see https://stackoverflow.com/questions/3979367
    toEOFilterValue: (data: string|null) => {
      if (data === null) {
        throw new Error("Cannot filter on null")
      }
      return "'" + data.replaceAll("'", "''") + "'"
    },
  })

export const StringTypeMap: TypeMap<string> = copyFilterEOValueToUpdateValue({
  fromEO: (data: string) => assertTM(d => typeof d === "string", data),
  // see https://stackoverflow.com/questions/3979367
  toEOFilterValue: (data: string) => "'" + data.replaceAll("'", "''") + "'",
})

export const ItemCodeTypeMap: TypeMap<ItemCode> = copyFilterEOValueToUpdateValue({
  fromEO: (data: string) => assertTM(d => typeof d === "string", data) as ItemCode,
  // see https://stackoverflow.com/questions/3979367
  toEOFilterValue: (data: ItemCode) => "'" + data.replaceAll("'", "''") + "'",
})

export const WarehouseCodeTypeMap: TypeMap<WarehouseCode> =
  copyFilterEOValueToUpdateValue({
    fromEO: (data: string) =>
    assertTM(d => typeof d === "string", data) as WarehouseCode,
    // see https://stackoverflow.com/questions/3979367
    toEOFilterValue:
    (data: WarehouseCode) => "'" + data.replaceAll("'", "''") + "'",
  })

export const NumberTypeMap: TypeMap<number> = copyFilterEOValueToUpdateValue({
  /**
   * Exact returns sometimes 35.999999999999 when it means 36.0... so rounding
   * on 10 digits, seems safe
   */
  fromEO: (data: number) => assertTM(
    d => typeof d === "number",
    Math.round(data * 1e10) / 1e10),
  toEOFilterValue: (data: number) => data.toString(),
})

export const NullableNumberTypeMap: TypeMap<number | null> =
  copyFilterEOValueToUpdateValue({
    fromEO: (data: number | null) =>
    data === null ? null : NumberTypeMap.fromEO(data),
    toEOFilterValue: (data: number | null) => {
      if (data === null) {
        throw new Error("Cannot filter on null")
      }
      return data.toString()
    },
  })

export const BooleanTypeMap: TypeMap<boolean> = copyFilterEOValueToUpdateValue({
  fromEO: (data: boolean) => assertTM(d => typeof d === "boolean", data),
  toEOFilterValue: (data: boolean) => data ? "true" : "false",
})

export const NumericBooleanTypeMap: TypeMap<0 | 1> =
  copyFilterEOValueToUpdateValue({
    fromEO: (data: 0 | 1) => assertTM(d => d === 0 || d === 1, data),
    toEOFilterValue: (data: 0 | 1) => data ? "1" : "0",
  })

export const NullableGuidTypeMap: TypeMap<Guid | null> =
  copyFilterEOValueToUpdateValue({
    fromEO: (data: string | null) => assertTM(
      d => d === null || (typeof d === "string" && !!d.match(GUID_RE)), data) as (
        Guid | null),
    toEOFilterValue: (data: Guid | null) =>
    "guid" + NullableStringTypeMap.toEOFilterValue(data)
  })

export const GuidTypeMap: TypeMap<Guid> = copyFilterEOValueToUpdateValue({
  fromEO: (data: string) => assertTM(
    d => typeof d === "string" && !!d.match(GUID_RE), data) as Guid,
  toEOFilterValue: (data: Guid) => "guid" + StringTypeMap.toEOFilterValue(data)
})

export const ISODateTypeMap: TypeMap<ISODate> = {
  fromEO: (data: string) => {
    assertTM(d => {
      if (typeof d !== "string") {
        return false
      }
      if (!(d.startsWith("/Date(") && d.endsWith(")/"))) {
        try {
          ISODate.parseISO(d)
        } catch {
          return false
        }
        return true
      }
        return true
    }, data)
    if (data.startsWith("/Date(")) {
      const timestamp = parseInt(
        data.slice("/Date(".length, data.length - ")/".length))
      return ISODate.fromUTCNativeDate(new Date(timestamp))
    }
    return ISODate.parseISO(data)
  },
  //toEOFilterValue: (data: ISODate) => `/Date(${data.toUTCNativeDate().valueOf()})/`,
  toEOFilterValue: (data: ISODate) => `datetime'${data.toISOString()}T00:00:00'`,
  toEOUpdateValue: data => data.toISOString(),
}

export const NullableISODateTypeMap: TypeMap<ISODate | null> =
  copyFilterEOValueToUpdateValue({
    fromEO: (data: string | null) => (
      data === null ? null : ISODateTypeMap.fromEO(data)),
    toEOFilterValue: (data: ISODate | null) => {
      if (data === null) {
        throw new Error("Cannot filter on null")
      }
      return ISODateTypeMap.toEOFilterValue(data)
    }
  })

interface CalcTypeMap<T> {
  validate: (data: string) => boolean
  parse: (data: string) => T
  unparse: (data: T) => string
}

export function apiToNotesFieldName(apiFieldName: string): string {
  const auto = apiFieldName.endsWith("AUTO")
  const baseFieldName = auto ? apiFieldName.slice(0, -"AUTO".length) : apiFieldName
  const kebabCaseFieldName =
    baseFieldName.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);
  return kebabCaseFieldName + (auto ? "-AUTO" : "")
}

export function getCalcData<CFTM extends CalcFieldTypeMap>(
  source: string | null,
  calcFieldTypeMap: CFTM,
): ApiObjectCalcField<CFTM>["$calc"] {
  const keyMap = new Map<string, keyof CFTM & string>(
    Object.keys(calcFieldTypeMap).map(key => [
      apiToNotesFieldName(key), key]))
  const noseCClines = (source ?? "")
    .split("\n")
    .filter(l => l.startsWith(">@@"))
  const $calc = {errors: new Array<string>()}
  noseCClines.forEach(line => {
    const split = line.indexOf(":")
    if (split === -1) {
      $calc.errors.push(`Invalid line: "${line}"`)
      return
    }
    const keywordNoteFormat = line.slice(0, split).slice(">@@".length).trim()
    const value = line.slice(split + 1).trim()
    if (!keyMap.has(keywordNoteFormat)) {
      $calc.errors.push(`Unknown keyword (for this object): "${line}"`)
      return
    }
    const keyword = keyMap.get(keywordNoteFormat)!
    const cftm = calcFieldTypeMap[keyword]
    if (!cftm.typemap.validate(value)) {
      $calc.errors.push(`Unable to parse "${value}" in "${line}"`)
      return
    }
    const parsedValue = cftm.typemap.parse(value)
    if (keyword in $calc) {
      $calc.errors.push(`Keyword provided twice: "${line}"`)
      return
    }
    ;($calc as any)[keyword] = parsedValue
  })
  Object.entries(calcFieldTypeMap).forEach(([keyword, cftm]) => {
    if (!(keyword in $calc)) {
      TSAssertType<{default: any}>(cftm)
      ;($calc as any)[keyword] = cftm.default
    }
  })
  return $calc as unknown as ApiObjectCalcField<CFTM>["$calc"]
}

export const YesNoCalcTypeMap: CalcTypeMap<boolean> = {
  validate: (data: string) => ["yes", "no"].indexOf(data.toLowerCase()) !== -1,
  parse: (data: string) => data.toLowerCase() === "yes",
  unparse: data => data ? "yes" : "no",
}

export const CSVListCalcTypeMap: CalcTypeMap<ReadonlyArray<string>> = {
  validate: (_data: string) => true,
  parse: (data: string) => data.split(",").map(s => s.trim()).filter(s => s !== ""),
  unparse: data => data.join(", "),
}

export const DateCalcTypeMap: CalcTypeMap<ISODate> = {
  validate: (data: string) => {
    try {
      ISODate.parseISO(data)
      return true;
    } catch {
      return false;
    }
  },
  parse: (data: string) => ISODate.parseISO(data),
  unparse: data => data.toISOString(),
}

export const NonNegIntCalcTypeMap: CalcTypeMap<number> = {
  validate: (data: string) => /^[0-9]+$/.test(data),
  parse: (data: string) => parseInt(data, 10),
  unparse: data => data.toString()
}


function getLinkCalcTypeMap(code: string): CalcTypeMap<[number, number]> {
  const regexp = new RegExp("^" + code + ":([1-9][0-9]*)\\(([1-9][0-9]*)\\)$")
  return {
    validate: (data: string) => regexp.test(data),
    parse: (data: string) => {
      const match = data.match(regexp)
      return [parseInt(match![1]), parseInt(match![2])]
    },
    unparse: data => `${code}:${data[0]}(${data[1]})`,
  }
}

export const PoLCalcTypeMap = getLinkCalcTypeMap("PO")
export const SoLCalcTypeMap = getLinkCalcTypeMap("SO")
export const WtLCalcTypeMap = getLinkCalcTypeMap("WT")
